/** @jsx jsx */
import React, {Fragment, useState} from "react"
import {jsx, css} from "@emotion/core"
import {useStaticQuery, graphql} from "gatsby"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import dayjs from "dayjs"
import "dayjs/locale/nl"

import PageWidth from "./page-width"

import {Page} from "../../types"

import {Theme} from "../styles/theme"

/* Theses pages and their descendants will show a sidebar. */
const WHITELISTED_PAGES = [
  "regio",
  "documenten",
  "themas",
  "programma-impuls-omgevingsveiligheid", // Renamed to...
  "meerjarenprogramma-versterking-omgevingsveiligheid", // ...this...and now to...
  "nieuw-meerjarenagenda-versterking-omgevingsveiligheid-2021-2024", // ...this
  "werkstandaarden",
]

interface ResultItem {
  node: SidebarItem
}

interface SidebarItem {
  title: string
  slug: string
  meta: {
    updatedAt: string
  }
  pageType?: {
    key: string
  }
  parentPage?: SidebarItem
}

interface Props {
  children?: React.ReactNode
  currentPage: Page
  landingPageType?: "themes-landing-page" | "documents-landing-page"
  rootTitle?: string
  alphabetised?: boolean
  navigation?: "dynamic"
}

const Sidebar = (props: Props): JSX.Element => {
  dayjs.locale("nl")
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false)

  const data = useStaticQuery(graphql`
    query AlphabetSidebarQuery {
      pages: allDatoCmsPage {
        edges {
          node {
            title
            slug
            meta {
              updatedAt
            }
            visual {
              url
            }
            parentPage {
              ... on DatoCmsPage {
                title
                slug
                pageType {
                  key
                }
                meta {
                  updatedAt
                }
              }
            }
          }
        }
      }
    }
  `)

  const childrenOfPage = (parent: {slug: string}): ResultItem[] => {
    return data.pages.edges
      .filter((page: ResultItem) => page.node.parentPage?.slug === parent.slug)
      .sort((a, b) => (a.node.title < b.node.title ? -1 : 1))
  }

  const updatedDateForPage = (page: ResultItem): string => {
    const allPages = childrenOfPage(page.node).concat([page])

    return allPages.reduce((prev, current) =>
      prev.node.meta.updatedAt > current.node.meta.updatedAt ? prev : current,
    ).node.meta.updatedAt
  }

  const firstLevelPages: ResultItem[] = data.pages.edges
    .filter((page: ResultItem) => {
      if (props.landingPageType) {
        return page.node.parentPage?.pageType?.key == props.landingPageType
      } else {
        if (
          WHITELISTED_PAGES.filter(
            page =>
              props.currentPage?.slug == page ||
              props.currentPage?.slug.startsWith(`${page}/`),
          ).length == 0
        ) {
          return false
        }

        return (
          props.currentPage?.slug == page.node.parentPage?.slug ||
          (props.currentPage?.parentPage &&
            props.currentPage.parentPage.slug == page.node.parentPage?.slug)
        )
      }
    })
    .sort((a, b) => (a.node.title < b.node.title ? -1 : 1))

  const renderSectionOverview = () => {
    return (
      <div css={SectionOverviewStyles}>
        <h3>{props.rootTitle}</h3>
        {firstLevelPages.map(page => (
          <a
            css={SectionCardStyles}
            key={page.node.slug}
            href={`/${page.node.slug}`}
          >
            <div>
              <div className="date">
                {dayjs(new Date(updatedDateForPage(page)))
                  .format("D MMMM YYYY")
                  .toLowerCase()}
              </div>
              <div className="title">{page.node.title}</div>
              <FontAwesomeIcon icon={["far", "arrow-right"]} />
            </div>
            {page.node.visual && (
              <figure
                style={{backgroundImage: `url(${page.node.visual.url}?w=500)`}}
              />
            )}
          </a>
        ))}
      </div>
    )
  }

  if (firstLevelPages.length > 0) {
    return (
      <div css={SidebarContainerStyles}>
        <div className="aligner white" />
        <PageWidth>
          <div css={SidebarStyles}>
            <div
              className={`mobile-menu ${!props.children ? "hidden" : ""}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              Inhoudsopgave
              <FontAwesomeIcon
                icon={["far", mobileMenuOpen ? "angle-down" : "angle-right"]}
              />
            </div>

            <Level1
              mobileMenuOpen={mobileMenuOpen}
              firstLevelPages={firstLevelPages}
              currentPage={props.currentPage}
              navigation={props.navigation}
              alphabetised={props.alphabetised}
              pages={data.pages}
            />
          </div>

          <div css={ContentStyles}>
            <div>
              {props.children ? props.children : renderSectionOverview()}
            </div>
          </div>
        </PageWidth>
        <div className="aligner gray" />
      </div>
    )
  } else {
    return <PageWidth text>{props.children}</PageWidth>
  }
}

interface Level1Props {
  mobileMenuOpen: boolean
  currentPage: Page
  alphabetised?: boolean
  navigation?: "dynamic"
  firstLevelPages: ResultItem[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pages: any // :-(
}

const Level1 = ({
  mobileMenuOpen,
  firstLevelPages,
  currentPage,
  alphabetised,
  navigation,
  pages,
}: Level1Props) => {
  const [openSections, setOpenSections] = useState<string[]>([])

  const sidebarSections = (): string[] => {
    const alphabet = [..."abcdefghijklmnopqrstuvwxyz"]
    const sections: string[] = []

    alphabet.forEach((letter, i) => {
      if (pagesForLetter(letter).length > 0 || i === 0) {
        sections.push(letter)
      } else if (pagesForLetter(String.fromCharCode(i + 96)).length === 0) {
        // Add letter to existing section if previous letter is also empty
        sections.push(`${(sections.pop() || "")[0]} - ${letter}`)
      } else {
        sections.push(letter)
      }
    })

    return sections
  }

  const pagesForLetter = (letter): ResultItem[] => {
    return firstLevelPages.filter(
      page => page.node.title[0].toLowerCase() === letter,
    )
  }

  const childrenOfPage = (parent: {slug: string}): ResultItem[] => {
    return pages.edges
      .filter((page: ResultItem) => page.node.parentPage?.slug === parent.slug)
      .sort((a, b) => (a.node.title < b.node.title ? -1 : 1))
  }

  const toggleSection = (slug: string) => {
    if (openSections.includes(slug)) {
      setOpenSections(openSections.filter(open => open !== slug))
    } else {
      setOpenSections(openSections.concat([slug]))
    }
  }

  const isCurrentSection = (page: ResultItem): boolean => {
    return (
      isCurrentPage(page.node.slug) ||
      childrenOfPage(page.node).filter(page => isCurrentPage(page.node.slug))
        .length > 0
    )
  }

  const isCurrentPage = (slug: string): boolean => {
    return slug === currentPage.slug
  }

  // Dynamic navigation means we show the child tree only as soon as it is active.
  if (currentPage.parentPage && navigation === "dynamic") {
    const top = firstLevelPages.find(
      page =>
        page.node.slug == currentPage.slug ||
        page.node.slug == currentPage.parentPage?.slug,
    )

    if (top) {
      const root = top.node.parentPage
      return (
        <ul className={mobileMenuOpen ? "" : "hidden"}>
          <li>
            {childrenOfPage(top.node).length > 0 && (
              <ul className={`level1 ${alphabetised ? "alphabetised" : ""}`}>
                <li>
                  <div>
                    <a href={`/${top.node.slug}`} className="active">
                      {top.node.title}
                    </a>

                    <div className="child-toggle">
                      <FontAwesomeIcon icon={["far", "angle-down"]} />
                    </div>
                  </div>
                  <Level2
                    tree={childrenOfPage(top.node)}
                    currentPage={currentPage}
                  />
                </li>
              </ul>
            )}
            <a href={`/${root?.slug}`}>Terug naar thema&apos;s</a>
          </li>
        </ul>
      )
    }
  }

  return (
    <ul className={mobileMenuOpen ? "" : "hidden"}>
      {sidebarSections().map(letter => (
        <li
          key={letter}
          className={pagesForLetter(letter).length > 0 ? "" : "empty"}
        >
          {alphabetised && <h4>{letter.toUpperCase()}</h4>}
          <ul className={`level1 ${alphabetised ? "alphabetised" : ""}`}>
            {pagesForLetter(letter).map(page => (
              <li key={page.node.slug}>
                <div>
                  <a
                    href={`/${page.node.slug}`}
                    className={isCurrentSection(page) ? "active" : ""}
                  >
                    {page.node.title}
                  </a>

                  {childrenOfPage(page.node).length > 0 && (
                    <div
                      className="child-toggle"
                      onClick={() => toggleSection(page.node.slug)}
                    >
                      <FontAwesomeIcon
                        icon={[
                          "far",
                          openSections.includes(page.node.slug) ||
                          isCurrentSection(page)
                            ? "angle-down"
                            : "angle-right",
                        ]}
                      />
                    </div>
                  )}
                </div>

                {(openSections.includes(page.node.slug) ||
                  isCurrentSection(page)) && (
                  <Level2
                    tree={childrenOfPage(page.node)}
                    currentPage={currentPage}
                  />
                )}
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  )
}

interface Level2Props {
  tree: ResultItem[]
  currentPage: Page
}

const Level2 = ({tree, currentPage}: Level2Props) => {
  const isCurrentPage = (slug: string): boolean => {
    return slug === currentPage.slug
  }

  if (tree.length === 0) {
    return null
  }

  return (
    <ul className="level2">
      {tree.map(page => (
        <li key={page.node.slug}>
          <FontAwesomeIcon icon={["far", "angle-right"]} />
          <a
            href={`/${page.node.slug}`}
            className={isCurrentPage(page.node.slug) ? "active" : ""}
          >
            {page.node.title}
          </a>
        </li>
      ))}
    </ul>
  )
}

export default Sidebar

const SidebarContainerStyles = css`
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;

  @media ${Theme.breakpoints.medium} {
    margin: -10rem 0;

    .page-width {
      display: flex;
      padding: 0;

      > div:first-of-type {
        padding-left: 3rem;
      }

      > div:last-of-type {
        padding-right: 3rem;
        overflow: hidden;
      }
    }

    .aligner,
    .page-width > * {
      padding: 12rem 0;
    }

    .aligner {
      flex-grow: 1;
      &.gray {
        background-color: ${Theme.colors.backgrounds.gray};
      }
    }
  }

  @media ${Theme.breakpoints.mediumDown} {
    .page-width {
      flex-direction: column;
    }

    .aligner {
      display: none;
    }
  }
`

const SidebarStyles = css`
  min-width: 35vw; // IE11
  min-width: min(35vw, 45rem);
  width: 35vw; // IE11
  width: min(35vw, 45rem);

  @media ${Theme.breakpoints.mediumDown} {
    width: 100%;
  }

  .mobile-menu {
    font-size: 2.5rem;
    margin-bottom: 3rem;
    cursor: pointer;
    &.hidden {
      display: none;
    }

    svg {
      margin-left: 2rem;
      color: ${Theme.colors.identity.primary};
    }
  }

  ul {
    padding-left: 0;
    list-style-type: none;
    &.alphabetised {
      margin-bottom: 4rem;
    }
    &.level1 {
      li {
        margin-bottom: 1.3rem;

        a.active {
          font-weight: 600;
        }

        > div {
          display: flex;
          align-items: center;

          .child-toggle {
            padding: 0 2rem;
            margin-right: -2rem;
            cursor: pointer;
            &:hover {
              opacity: 0.7;
            }
          }
        }
      }
    }
    &.level2 {
      margin: 2rem 0;

      li {
        display: flex;
        margin-left: 3rem;

        svg {
          margin-right: 3rem;
        }

        a.active {
          color: ${Theme.colors.identity.primary};
        }
      }
    }

    svg {
      font-size: 2.3rem;
      color: ${Theme.colors.identity.primary};
    }

    h4 {
      margin: 2rem 0 1rem;
      font-size: 2.5rem;
      line-height: 3rem;
      font-weight: 700;
    }

    li.empty h4 {
      opacity: 0.5;
    }

    a {
      color: ${Theme.colors.fontcolors.body};
      font-size: 2rem;
      line-height: 2.4rem;
      &:hover {
        color: ${Theme.colors.identity.primary};
      }
    }
  }

  @media ${Theme.breakpoints.medium} {
    > ul {
      max-width: 45rem;
      padding-right: 3rem;
    }

    .mobile-menu {
      display: none;
    }
  }

  @media ${Theme.breakpoints.mediumDown} {
    > ul.hidden {
      display: none;
    }
  }
`

const ContentStyles = css`
  flex-grow: 1;
  background-color: ${Theme.colors.backgrounds.gray};

  @media ${Theme.breakpoints.medium} {
    > div {
      padding-left: 8rem; // IE11
      padding-left: min(6vw, 8rem);
    }
  }

  @media ${Theme.breakpoints.mediumDown} {
    background-color: white;
  }
`

const SectionOverviewStyles = css`
  @media ${Theme.breakpoints.mediumDown} {
    display: none;
  }
`

const SectionCardStyles = css`
  display: flex;
  width: 100%;
  margin-bottom: 3rem;
  box-shadow: 0 0 2rem rgba(106, 106, 106, 0.3);
  border-radius: 0.2rem;
  background: white;
  &:hover {
    text-decoration: none;

    .title {
      text-decoration: underline;
    }
  }

  > div {
    flex-basis: 60%;
    flex-grow: 1;
    padding: 3rem 3rem 2rem;

    .date {
      margin-bottom: 1.5rem;
      font-size: 1rem;
      line-height: 1.1rem;
      text-transform: uppercase;
      color: ${Theme.colors.identity.primary};
      font-weight: 700;
    }

    .title {
      margin-bottom: 2rem;
      font-size: 2rem;
      font-weight: 500;
      line-height: 2.6rem;
      color: ${Theme.colors.fontcolors.heading};
    }

    svg {
      color: ${Theme.colors.identity.primary};
    }
  }

  > figure {
    flex-basis: 40%;
    background-position: center;
    background-size: cover;
    border-top-right-radius: 0.2rem;
    border-bottom-right-radius: 0.2rem;

    @media ${Theme.breakpoints.mediumDown} {
      display: none;
    }
  }
`
